<template>
  <ig-page-base>
    <div class="box invoice-configuration__content">
      <div class="invoice-configuration__title-box">
        <h2 class="title invoice-configuration__title has-text-weight-semibold">
          {{ $t('storePages.invoiceConfiguration.title') }}
        </h2>
      </div>

      <div class="invoice-configuration__form">
        <form @submit.prevent="updateConfiguration(configuration)">
          <div class="columns">
            <div class="column is-6">
              <div class="columns is-flex-direction-column">
                <div class="column">
                  <div class="invoice-configuration__drop-box">
                    <ig-file-upload
                      ref="fileUpload"
                      :labelIdle="$t('storePages.invoiceConfiguration.form.certificate.dropLabel')"
                      :acceptedFileTypes="[
                        'application/x-pkcs12',
                        'application/x-pkcs12-certificates'
                      ]"
                      :files="certFiles"
                      maxFileSize="50KB"
                      :server="serverUpload(preSignedUrlCertificate, 'cert')"
                      :allowReplace="false"
                      :allowRevert="false"
                      :allowReorder="false"
                      credits=""
                    />

                    <div class="has-text-right">
                      <b-button
                        type="is-text"
                        icon-left="upload"
                        @click="chooseFile('fileUpload')"
                      >
                        {{
                          $t('storePages.invoiceConfiguration.form.certificate.btnChooseFile')
                        }}
                      </b-button>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <b-field
                    :label="$t('storePages.invoiceConfiguration.form.certificatePassword.label')"
                    label-position="inside"
                  >
                    <b-input
                      id="certPassword"
                      type="password"
                      password-reveal
                      v-model="configuration.certPassword"
                      :disabled="loading"
                      expanded
                    />
                  </b-field>
                </div>
              </div>
            </div>
            <div class="column is-6">
              <div class="columns">
                <div class="column">
                  <div class="invoice-configuration__drop-box">
                    <ig-file-upload
                      ref="fileUploadLogo"
                      :labelIdle="$t('storePages.invoiceConfiguration.form.logo.dropLabel')"
                      :acceptedFileTypes="[
                        'image/png',
                      ]"
                      maxFileSize="500KB"
                      :files="logoFiles"
                      :server="serverUpload(preSignedUrlLogo, 'logo')"
                      :allowReplace="false"
                      :allowRevert="false"
                      :allowReorder="false"
                      credits=""
                    />

                    <div class="has-text-right">
                      <b-button
                        type="is-text"
                        icon-left="upload"
                        @click="chooseFile('fileUploadLogo')"
                      >
                        {{
                          $t('storePages.invoiceConfiguration.form.logo.btnChooseFile')
                        }}
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <h1>{{ $t('storePages.invoiceConfiguration.block.invoice') }}</h1>
            </div>
          </div>
          <div class="columns">
            <div class="column is-4">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.number.label')"
                label-position="inside"
              >
                <b-input
                  id="number"
                  type="number"
                  maxlength="9"
                  v-model="configuration.nextNumber"
                  :placeholder="$t('storePages.invoiceConfiguration.form.number.placeholder')"
                  :disabled="loading"
                  expanded
                />
              </b-field>
            </div>
            <div class="column is-4">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.series.label')"
                label-position="inside"
              >
                <b-input
                  id="series"
                  type="number"
                  maxlength="3"
                  v-model="configuration.series"
                  :placeholder="$t('storePages.invoiceConfiguration.form.series.placeholder')"
                  :disabled="loading"
                  expanded
                />
              </b-field>
            </div>

            <div class="column is-4">
              <b-field :label="$t('storePages.invoiceConfiguration.form.environment.label')">
                <b-switch
                  v-model="configuration.environment"
                  true-value="PRODUCTION"
                  false-value="SANDBOX"
                  :disabled="loading"
                >
                  {{ $t( [
                    'storePages.invoiceConfiguration.form.environment',
                    configuration.environment,
                    ].join('.'))
                  }}
                </b-switch>
              </b-field>
            </div>
          </div>
          <div class="columns">
            <div class="column is-4">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.natureOperation.label')"
                label-position="inside"
              >
                <b-input
                  id="natureOperation"
                  v-model="configuration.natureOperation"
                  :placeholder="$t(
                    'storePages.invoiceConfiguration.form.natureOperation.placeholder'
                  )"
                  :disabled="loading"
                  expanded
                />
              </b-field>
            </div>
            <div class="column is-4">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.taxRegime.label')"
                label-position="inside"
              >
                <b-select
                  :placeholder="$t('storePages.invoiceConfiguration.form.taxRegime.placeholder')"
                  v-model="configuration.taxRegime"
                  :disabled="loading"
                  expanded>
                  <option value="SIMPLES_NACIONAL">
                    {{ $t('storePages.invoiceConfiguration.form.taxRegime.SIMPLES_NACIONAL') }}
                  </option>
                  <option value="SIMPLES_NACIONAL_EXCESSO">
                    {{
                      $t('storePages.invoiceConfiguration.form.taxRegime.SIMPLES_NACIONAL_EXCESSO')
                    }}
                  </option>
                  <option value="REGIME_NORMAL">
                    {{ $t('storePages.invoiceConfiguration.form.taxRegime.REGIME_NORMAL') }}
                  </option>
                  <option value="MEI">
                    {{ $t('storePages.invoiceConfiguration.form.taxRegime.MEI') }}
                  </option>
                </b-select>
              </b-field>
            </div>

            <div class="column is-4">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.cnae.label')"
                label-position="inside"
              >
                <b-input
                  id="cnae"
                  type="number"
                  maxlength="7"
                  v-model="configuration.cnae"
                  :placeholder="$t(
                    'storePages.invoiceConfiguration.form.cnae.placeholder'
                  )"
                  :disabled="loading"
                  expanded
                />
              </b-field>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.observation.label')"
                label-position="inside"
              >
                <b-input
                  v-model="configuration.observation"
                  maxlength="5000"
                  type="textarea"
                ></b-input>
              </b-field>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <h1>{{ $t('storePages.invoiceConfiguration.block.taxes') }}</h1>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <b-tabs>
                <b-tab-item :label="$t('storePages.invoiceConfiguration.form.tabs.icms')">
                  <div class="columns">
                    <div class="column" v-if="configuration.taxRegime === 'REGIME_NORMAL'">
                      <b-field
                        :label="$t('storePages.invoiceConfiguration.form.tabs.fields.cst.label')"
                        label-position="inside"
                      >
                        <ig-tree-select
                          v-model="configuration.icmsCst"
                          :options="listIcmsCst"
                          clearable
                          searchable
                          expanded
                        />
                      </b-field>
                    </div>
                    <div class="column" v-else>
                      <b-field
                        :label="$t('storePages.invoiceConfiguration.form.tabs.fields.csosn.label')"
                        label-position="inside"
                      >
                        <ig-tree-select
                          v-model="configuration.icmsCsosn"
                          :options="listCsosn"
                          clearable
                          searchable
                          expanded
                        />
                      </b-field>
                    </div>
                  </div>
                  <div class="columns">
                    <div class="column is-4">
                      <b-field
                        :label="$t('storePages.invoiceConfiguration.form.tabs.fields.cfop.label')"
                        label-position="inside"
                      >
                        <b-input
                          id="cfop"
                          type="number"
                          maxlength="4"
                          v-model="configuration.cfop"
                          :placeholder="$t(
                            'storePages.invoiceConfiguration.form.tabs.fields.cfop.placeholder'
                           )"
                          :disabled="loading"
                          expanded
                        />
                      </b-field>
                    </div>
                  </div>
                </b-tab-item>

                <b-tab-item :label="$t('storePages.invoiceConfiguration.form.tabs.ipi')">
                  <div class="columns">
                    <div class="column">
                      <b-field
                        :label="$t('storePages.invoiceConfiguration.form.tabs.fields.cst.label')"
                        label-position="inside"
                      >
                        <ig-tree-select
                          v-model="configuration.ipiCst"
                          :options="listIpiCst"
                          clearable
                          searchable
                          expanded
                        />
                      </b-field>
                    </div>
                  </div>
                </b-tab-item>

                <b-tab-item :label="$t('storePages.invoiceConfiguration.form.tabs.pis')">
                  <div class="columns">
                    <div class="column">
                      <b-field
                        :label="$t('storePages.invoiceConfiguration.form.tabs.fields.cst.label')"
                        label-position="inside"
                      >
                        <ig-tree-select
                          v-model="configuration.pisCst"
                          :options="listPisAndCofinsCst"
                          clearable
                          searchable
                          expanded
                        />
                      </b-field>
                    </div>
                  </div>
                </b-tab-item>

                <b-tab-item :label="$t('storePages.invoiceConfiguration.form.tabs.cofins')">
                  <div class="columns">
                    <div class="column">
                      <b-field
                        :label="$t('storePages.invoiceConfiguration.form.tabs.fields.cst.label')"
                        label-position="inside"
                      >
                        <ig-tree-select
                          v-model="configuration.cofinsCst"
                          :options="listPisAndCofinsCst"
                          clearable
                          searchable
                          expanded
                        />
                      </b-field>
                    </div>
                  </div>
                </b-tab-item>
              </b-tabs>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <h1>{{ $t('storePages.invoiceConfiguration.block.exportation') }}</h1>
            </div>
          </div>
          <div class="columns">
            <div class="column is-4">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.exportationCity.label')"
                label-position="inside"
              >
                <b-input
                  id="exportationCity"
                  v-model="configuration.exportationCity"
                  :placeholder="$t(
                    'storePages.invoiceConfiguration.form.exportationCity.placeholder'
                  )"
                  :disabled="loading"
                  expanded
                />
              </b-field>
            </div>
            <div class="column is-4">
              <b-field
                :label="$t('storePages.invoiceConfiguration.form.exportationStateCode.label')"
                label-position="inside"
              >
                <ig-state-dropdown
                  v-model="configuration.exportationStateCode"
                  :placeholder="$t(
                    'storePages.invoiceConfiguration.form.exportationStateCode.placeholder'
                  )"
                  :disabled="loading"
                  expanded/>
              </b-field>
            </div>
          </div>

          <div class="has-text-right">
            <b-button
              native-type="submit"
              class="is-uppercase"
              :label="$t('storePages.invoiceConfiguration.form.btnSubmit')"
              :loading="loadingSave"
              :disabled="loadingSave"
              type="is-primary"></b-button>
          </div>
        </form>
      </div>
    </div>
  </ig-page-base>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import {
  EventBus, IgFileUpload, IgPageBase, IgTreeSelect, IgStateDropdown,
} from '@integrai/ui';

const { mapState, mapActions } = createNamespacedHelpers('invoiceConfiguration');

export default Vue.extend({
  name: 'page-invoice-configuration',
  components: {
    IgFileUpload,
    IgTreeSelect,
    IgPageBase,
    IgStateDropdown,
  },
  data() {
    return {
      configuration: {
        environment: 'SANDBOX',
        cfop: '7101',
        icmsCst: '41',
        icmsCsosn: '400',
        ipiCst: '54',
        pisCst: '08',
        cofinsCst: '08',
      },
      listCsosn: [
        { id: '101', label: '101 - Tributada com permissão de crédito' },
        { id: '102', label: '102 - Tributada sem permissão de crédito' },
        { id: '103', label: '103 - Isenção do ICMS para faixa de receita bruta' },
        { id: '201', label: '201 - Tributada com permissão de crédito e com cobrança do ICMS por ST' },
        { id: '202', label: '202 - Tributada sem permissão de crédito e com cobrança do ICMS por ST' },
        { id: '203', label: '203 - Isenção do ICMS para faixa de receita bruta e com cobrança do ICMS por ST' },
        { id: '300', label: '300 - Imune' },
        { id: '400', label: '400 - Não tributada' },
        { id: '500', label: '500 - ICMS cobrado anteriormente por ST ou por antecipação' },
        { id: '900', label: '900 - Outros' },
      ],
      listIcmsCst: [
        { id: '00', label: '00 - Tributada integralmente' },
        { id: '10', label: '10 - Tributada e com cobrança do ICMS por substituição tributária' },
        { id: '20', label: '20 - Com redução de base de cálculo' },
        { id: '30', label: '30 - Isenta ou não tributada e com cobrança do ICMS por substituição tributária' },
        { id: '40', label: '40 - Isenta' },
        { id: '41', label: '41 - Não tributada' },
        { id: '50', label: '50 - Suspensão' },
        { id: '51', label: '51 - Diferimento' },
        { id: '60', label: '60 - ICMS cobrado anteriormente por substituição tributária' },
        { id: '70', label: '70 - Com redução de base de cálculo e cobrança do ICMS por substituição tributária' },
        { id: '90', label: '90 - Outras' },
      ],
      listIpiCst: [
        { id: '50', label: '50 - Saída tributada' },
        { id: '51', label: '51 - Saída tributada com alíquota zero' },
        { id: '52', label: '52 - Saída isenta' },
        { id: '53', label: '53 - Saída não-tributada' },
        { id: '54', label: '54 - Saída imune' },
        { id: '55', label: '55 - Saída com suspensão' },
        { id: '99', label: '99 - Outras saídas' },
        { id: 'ZZ', label: 'Não destacar IPI' },
      ],
      listPisAndCofinsCst: [
        { id: '01', label: '01 - Operação tributável (alíquota normal, cumulativo ou não)' },
        { id: '02', label: '02 - Operação tributável (alíquota diferenciada)' },
        { id: '03', label: '03 - Operação tributável (alíquota por unidade de produto)' },
        { id: '04', label: '04 - Operação tributável (tributação monofásica, alíquota zero)' },
        { id: '05', label: '05 - Operação Tributável (Substituição Tributária)' },
        { id: '06', label: '06 - Operação tributável (alíquota zero)' },
        { id: '07', label: '07 - Operação isenta da contribuição' },
        { id: '08', label: '08 - Operação sem incidência da contribuição' },
        { id: '09', label: '09 - Operação com suspensão da contribuição' },
        { id: '49', label: '49 - Outras Operações de Saída' },
        { id: '50', label: '50 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
        { id: '51', label: '51 - Operação com Direito a Crédito – Vinculada Exclusivamente a Receita Não Tributada no Mercado Interno' },
        { id: '52', label: '52 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita de Exportação' },
        { id: '53', label: '53 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
        { id: '54', label: '54 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
        { id: '55', label: '55 - Operação com Direito a Crédito - Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação' },
        { id: '56', label: '56 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação' },
        { id: '60', label: '60 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Tributada no Mercado Interno' },
        { id: '61', label: '61 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno' },
        { id: '62', label: '62 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita de Exportação' },
        { id: '63', label: '63 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno' },
        { id: '64', label: '64 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas no Mercado Interno e de Exportação' },
        { id: '65', label: '65 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação' },
        { id: '66', label: '66 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação' },
        { id: '67', label: '67 - Crédito Presumido - Outras Operações' },
        { id: '70', label: '70 - Operação de Aquisição sem Direito a Crédito' },
        { id: '71', label: '71 - Operação de Aquisição com Isenção' },
        { id: '72', label: '72 - Operação de Aquisição com Suspensão' },
        { id: '73', label: '73 - Operação de Aquisição a Alíquota Zero' },
        { id: '74', label: '74 - Operação de Aquisição sem Incidência da Contribuição' },
        { id: '75', label: '75 - Operação de Aquisição por Substituição Tributária' },
        { id: '98', label: '98 - Outras Operações de Entrada' },
        { id: '99', label: '99 - Outras operações' },
      ],
    };
  },
  computed: {
    ...mapState([
      'loading',
      'loadingSave',
      'invoiceConfiguration',
      'preSignedUrlCertificate',
      'preSignedUrlLogo',
    ]),
    logoFiles() {
      return this.invoiceConfiguration.logo ? [{
        source: this.invoiceConfiguration.logo,
        file: {
          name: 'logo.png',
          type: 'image/png',
        },
        options: {
          type: 'local',
        },
      }] : [];
    },
    certFiles() {
      return this.invoiceConfiguration.cert ? [{
        source: this.invoiceConfiguration.cert,
        file: {
          name: 'certificate.pdf',
          type: 'application/x-pkcs12',
        },
        options: {
          type: 'local',
        },
      }] : [];
    },
  },
  methods: {
    ...mapActions([
      'getInvoiceConfiguration',
      'getPreSignedUrl',
      'updateInvoiceConfiguration',
    ]),
    serverUpload(preSignedUrl, modelName) {
      return {
        process: (
          fieldName,
          file,
          metadata,
          load,
          error,
          progress,
          abort,
        ) => {
          const request = new XMLHttpRequest();

          request.open('PUT', preSignedUrl, true);

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = () => {
            if (request.status >= 200 && request.status < 300) {
              let fileUrl = request.responseURL;

              if (fileUrl.includes('?')) {
                fileUrl = fileUrl.split('?').shift();
              }

              this.configuration[modelName] = fileUrl;

              load(request.responseURL);
            } else {
              error('oh no');
            }
          };

          request.send(file);

          return {
            abort: () => {
              request.abort();
              abort();
            },
          };
        },
        load: (source, load) => {
          fetch(source).then((res) => res.blob()).then(load);
        },
      };
    },
    async updateConfiguration(configuration) {
      await this.updateInvoiceConfiguration(configuration);

      EventBus.$emit('toast', {
        type: 'is-success',
        message: this.$t('storePages.invoiceConfiguration.form.messageSuccess'),
      });
    },
    chooseFile(field) {
      (this.$refs[field]).browse();
    },
  },
  async beforeMount() {
    await this.getPreSignedUrl();
    await this.getInvoiceConfiguration();

    this.$nextTick(() => {
      this.configuration = {
        ...this.configuration,
        ...this.invoiceConfiguration,
      };
    });
  },
});
</script>

<style lang="scss">
  .invoice-configuration {
    &__form{
      margin: 2rem 0;
    }
    &__drop-box{
      margin: 1rem;
      max-height: 350px;
      overflow-y: auto;
      box-shadow: inset rgba(143,160,241,0.2) 0 0 0 1px, rgba(213,220,247,0.59) 0 10px 20px;
      border-radius: 10px;
      cursor: pointer;
    }
    &__drop-title{
      font-size: 1.7rem!important;
      font-weight: 600;
      margin-bottom: 0.5rem;
      cursor: pointer;
      .svg-inline--fa{
        display: block;
        margin: 0.5rem auto;
        font-size: 2rem;
      }
    }
    &__drop-subtitle{
      font-size: 0.8rem!important;
      margin-bottom: 3rem;
      cursor: pointer;
    }
  }

  .filepond--image-preview{
    background: #fff!important;
  }
</style>
